import React from 'react';
import ActivityChart from './ActivityChart';
import OxygenChart from './OxygenChart';
import TemperatureChart from './TemperatureChart';
import CurrentChart from './CurrentChart';
import { DetailHoverSummary } from '../DetailTooltips';
import { buildDetailTooltipMetrics } from '../../utils/detailTooltipMetrics';

/**
 * Renderiza el bloque DailyMetricCharts de SCAPP V2.
 *
 * @param {object} props
 * @param {object} props.v - Modelo de presentación y callbacks del panel.
 * @returns {React.ReactNode} Bloque visual sin estado local.
 */
function DetailMetricLegend({ v }) {
  const metrics = [{
    key: 'activity', label: 'Actividad [%]', color: 'oklch(0.78 0.14 160)', available: v.hasActivity, active: v.showActivitySeries
  }, {
    key: 'rate', label: 'Tasa [kg/min]', color: 'oklch(0.72 0.14 55)', available: v.hasRate, active: v.showRateSeries
  }, {
    key: 'ppa', label: 'PPA acum. [pellets]', color: 'oklch(0.68 0.2 25)', available: v.hasPpa, active: v.showPpaSeries
  }, {
    key: 'oxygen', label: 'Oxígeno [' + (v.det.oxygenUnit || '%') + ']', color: 'oklch(0.78 0.11 220)', available: v.hasOxygen, active: v.showOxygenSeries
  }, {
    key: 'temperature', label: 'Temp. [°C]', color: 'oklch(0.74 0.13 340)', available: v.hasTemperature, active: v.showTemperatureSeries
  }, {
    key: 'current', label: 'Corriente [cm/s]', color: 'oklch(0.72 0.14 295)', available: v.hasCurrent, active: v.showCurrentSeries
  }, {
    key: 'direction', label: 'Dirección', color: 'oklch(0.72 0.14 295)', available: v.hasDirection, active: v.showDirectionSeries, direction: true
  }].filter(metric => metric.available);
  return React.createElement("div", {
    className: "scapp-detail-legend-row",
    "aria-label": "Leyenda de métricas",
    style: {
      "display": "flex", "alignItems": "center", "gap": "1.25vw",
      "minHeight": "2.407407vh", "overflowX": "auto", "overflowY": "hidden",
      "flex": "none", "padding": "0 0.208333vw"
    }
  }, metrics.map(metric => React.createElement("button", {
    key: metric.key, type: "button", "data-detail-legend": metric.key, "aria-pressed": metric.active,
    title: (metric.active ? "Ocultar " : "Mostrar ") + metric.label,
    onClick: () => v.toggleDetailMetric(metric.key),
    style: {
      "display": "inline-flex", "alignItems": "center", "gap": "0.520833vw",
      "flex": "none", "border": "0", "background": "transparent",
      "padding": "0.277778vh 0", "color": "#b8c1ce", "fontSize": "1.203704vh",
      "whiteSpace": "nowrap", "cursor": "pointer", "opacity": metric.active ? "1" : "0.38"
    }
  }, React.createElement("span", {
    "aria-hidden": "true",
    style: metric.direction ? {
      "width": "0.833333vw", "color": metric.color, "fontWeight": "700", "textAlign": "center"
    } : {
      "width": "0.833333vw", "height": "0.277778vh", "borderRadius": "0.185185vh", "background": metric.color
    }
  }, metric.direction ? "↖" : null), v.text(metric.label))));
}

export default function DailyMetricCharts({
  v
}) {
  const detailTooltipMetrics = buildDetailTooltipMetrics(v);
  return React.createElement("div", {
    className: "scapp-detail-charts",
    style: {
      "display": "flex",
      "flex": "1",
      "minHeight": "0",
      "flexDirection": "column",
      "gap": "0.555556vh",
      "position": "relative",
      "zIndex": "20",
      "overflow": "visible",
      "height": "100%"
    }
  }, React.createElement(DetailHoverSummary, {
    values: v,
    metrics: detailTooltipMetrics,
    alarm: v.alL,
    note: v.noteL
  }), v.text("\n          "), React.createElement(DetailMetricLegend, {
    v
  }), v.text("\n          "), React.createElement(ActivityChart, {
    v
  }), v.text("\n          "), React.createElement(OxygenChart, {
    v
  }), v.text("\n          "), React.createElement(TemperatureChart, {
    v
  }), v.text("\n          "), React.createElement(CurrentChart, {
    v
  }), v.text("\n          "), React.createElement("div", {
    style: {
      "display": "grid",
      "gridTemplateColumns": "13.541667vw 2.291667vw minmax(0,1fr) 4.791667vw",
      "gap": "0"
    }
  }, v.text("\n            "), React.createElement("div", {
    style: {
      "fontSize": "1.018519vh",
      "color": "#5a667a"
    }
  }, v.text(v.zLabel)), v.text("\n            "), React.createElement("div", {
    style: {
      "gridColumn": "3",
      "display": "flex",
      "justifyContent": "space-between",
      "font": "1.018519vh 'IBM Plex Mono',monospace",
      "color": "#5a667a"
    }
  }, (v.zTicks || []).map((t, index35) => React.createElement(React.Fragment, {
    key: index35
  }, React.createElement("span", {}, v.text(t))))), v.text("\n          ")), v.text("\n          "), v.zBarOn ? React.createElement(React.Fragment, null, v.text("\n            "), React.createElement("div", {
    style: {
      "display": "grid",
      "gridTemplateColumns": "13.541667vw 2.291667vw minmax(0,1fr) 4.791667vw",
      "gap": "0",
      "marginTop": "0.37037vh"
    }
  }, v.text("\n              "), React.createElement("div", {}, null), React.createElement("div", {}, null), v.text("\n              "), React.createElement("div", {
    "onMouseDown": v.zbDown,
    "onMouseMove": v.zbMove,
    "onMouseUp": v.zbUp,
    "onMouseLeave": v.zbUp,
    style: {
      "gridColumn": "3",
      "position": "relative",
      "height": "1.481481vh",
      "borderRadius": "0.740741vh",
      "background": "rgba(4,12,24,0.55)",
      "border": "0.092593vh solid #1c2636",
      "cursor": "pointer",
      "userSelect": "none"
    }
  }, v.text("\n                "), React.createElement("div", {
    style: {
      "position": "absolute",
      "top": "0.185185vh",
      "bottom": "0.185185vh",
      "left": v.zbL,
      "width": v.zbW,
      "minWidth": "1.25vw",
      "borderRadius": "0.555556vh",
      "background": "#3a475c",
      "cursor": "grab"
    },
    className: "scapp-full-hover"
  }, null), v.text("\n              ")), v.text("\n            ")), v.text("\n          ")) : null, v.text("\n        "));
}
